import { ProjectModel, ProjectStatus } from '../types/projects';
import { WorkOrder, WorkOrderStatus } from '../types/workOrders';

/**
 * Pure helpers behind the project board (Pages/Projects/ProjectBoardPage):
 * delmoment ordering, per-project summaries, the urgency rule and the column
 * sort orders. Everything takes `today` as a `YYYY-MM-DD` string so the rules
 * are testable without touching the clock.
 */

/** Aggregated delmoment (work order) state for one project. */
export interface DelmomentSummary {
    /** Linked, non-cancelled work orders (open + completed). */
    total: number;
    /** Linked work orders with status COMPLETED. */
    completed: number;
    /** Open work orders whose due date has passed. */
    overdue: number;
    /** Earliest due date (YYYY-MM-DD) among open work orders not yet overdue. */
    nextDue?: string;
}

/** Date part of a stored date, which may carry a time (`YYYY-MM-DD hh:mm:ss`). */
export function datePart(dateStr: string): string {
    return dateStr.slice(0, 10);
}

/**
 * Whole days from `today` to `dateStr` (both date-only or date+time strings).
 * Negative when the date has passed. Parsed as local calendar dates so no
 * timezone offset leaks into the day count.
 */
export function daysFromToday(today: string, dateStr: string): number {
    const toLocalDate = (s: string): Date => {
        const [y, m, d] = datePart(s).split('-').map(Number);
        return new Date(y, m - 1, d);
    };
    const diff = toLocalDate(dateStr).getTime() - toLocalDate(today).getTime();
    return Math.round(diff / (1000 * 60 * 60 * 24));
}

/** True for an open work order whose due date is before today. */
export function isOverdueDelmoment(workOrder: WorkOrder, today: string): boolean {
    return workOrder.status === WorkOrderStatus.OPEN
        && !!workOrder.dueDate
        && daysFromToday(today, workOrder.dueDate) < 0;
}

/**
 * The flat list order inside an expanded project card: open work orders by due
 * date (so overdue ones lead, most overdue first), then open ones without a due
 * date (oldest created first), then completed ones (latest completion first).
 * Cancelled work orders are not part of the project's scope and are dropped.
 */
export function sortDelmoment<T extends WorkOrder>(workOrders: T[]): T[] {
    const rank = (wo: T): number => {
        if (wo.status === WorkOrderStatus.COMPLETED) return 2;
        return wo.dueDate ? 0 : 1;
    };
    return workOrders
        .filter(wo => wo.status !== WorkOrderStatus.CANCELLED)
        .sort((a, b) => {
            const ra = rank(a);
            const rb = rank(b);
            if (ra !== rb) return ra - rb;
            switch (ra) {
                case 0:
                    return datePart(a.dueDate!).localeCompare(datePart(b.dueDate!)) || a.title.localeCompare(b.title, 'sv');
                case 1:
                    return (a.createdAt ?? '').localeCompare(b.createdAt ?? '') || a.title.localeCompare(b.title, 'sv');
                default:
                    return (b.completedAt ?? '').localeCompare(a.completedAt ?? '') || a.title.localeCompare(b.title, 'sv');
            }
        });
}

/** Summarise a project's delmoment. Cancelled work orders are ignored. */
export function summarizeDelmoment(workOrders: WorkOrder[], today: string): DelmomentSummary {
    const summary: DelmomentSummary = { total: 0, completed: 0, overdue: 0 };
    let nextDue: string | undefined;
    workOrders.forEach(wo => {
        if (wo.status === WorkOrderStatus.CANCELLED) return;
        summary.total += 1;
        if (wo.status === WorkOrderStatus.COMPLETED) {
            summary.completed += 1;
            return;
        }
        if (!wo.dueDate) return;
        if (daysFromToday(today, wo.dueDate) < 0) {
            summary.overdue += 1;
        } else {
            const due = datePart(wo.dueDate);
            if (!nextDue || due < nextDue) nextDue = due;
        }
    });
    if (nextDue) summary.nextDue = nextDue;
    return summary;
}

/**
 * Days until the project's planned end; negative once it has passed. Only
 * meaningful while the project is not completed.
 */
export function projectDaysLeft(project: Pick<ProjectModel, 'plannedEndDate'>, today: string): number | null {
    if (!project.plannedEndDate) return null;
    return daysFromToday(today, project.plannedEndDate);
}

/**
 * The urgency rule: a project in progress is urgent when any open delmoment is
 * overdue or the project itself is past its planned end date. Urgent projects
 * start expanded on the board and sort first in the Pågående column.
 */
export function isUrgentProject(
    project: Pick<ProjectModel, 'status' | 'plannedEndDate'>,
    summary: DelmomentSummary | undefined,
    today: string
): boolean {
    if (project.status !== ProjectStatus.IN_PROGRESS) return false;
    if (summary && summary.overdue > 0) return true;
    const daysLeft = projectDaysLeft(project, today);
    return daysLeft !== null && daysLeft < 0;
}

/**
 * How overdue an urgent project is, as the most negative day count among its
 * overdue delmoment and its own planned end. Lower sorts first. Returns 0 for
 * projects that are not urgent.
 */
export function urgencyDays(
    project: Pick<ProjectModel, 'status' | 'plannedEndDate'>,
    workOrders: WorkOrder[],
    today: string
): number {
    let worst = 0;
    workOrders.forEach(wo => {
        if (!isOverdueDelmoment(wo, today)) return;
        worst = Math.min(worst, daysFromToday(today, wo.dueDate!));
    });
    const daysLeft = projectDaysLeft(project, today);
    if (daysLeft !== null && daysLeft < 0) worst = Math.min(worst, daysLeft);
    return worst;
}

const byTitle = <T extends { title: string }>(a: T, b: T): number => a.title.localeCompare(b.title, 'sv');

/**
 * Pågående column order: urgent projects first (most overdue first), then by
 * planned end date ascending, then title.
 */
export function sortInProgressProjects<T extends ProjectModel>(
    projects: T[],
    delmomentByProject: Map<string, WorkOrder[]>,
    today: string
): T[] {
    const urgency = new Map<string, number>();
    projects.forEach(p => {
        const workOrders = delmomentByProject.get(p.key) ?? [];
        const urgent = isUrgentProject(p, summarizeDelmoment(workOrders, today), today);
        urgency.set(p.key, urgent ? urgencyDays(p, workOrders, today) : 0);
    });
    return [...projects].sort((a, b) => {
        const ua = urgency.get(a.key) ?? 0;
        const ub = urgency.get(b.key) ?? 0;
        const aUrgent = ua < 0;
        const bUrgent = ub < 0;
        if (aUrgent !== bUrgent) return aUrgent ? -1 : 1;
        if (aUrgent && ua !== ub) return ua - ub;
        return (a.plannedEndDate ?? '').localeCompare(b.plannedEndDate ?? '') || byTitle(a, b);
    });
}

/** Planerade column order: start date ascending, then title. */
export function sortPlannedProjects<T extends ProjectModel>(projects: T[]): T[] {
    return [...projects].sort((a, b) =>
        (a.startDate ?? '').localeCompare(b.startDate ?? '') || byTitle(a, b)
    );
}

/** Utförda column order: actual end date descending (newest first), then title. */
export function sortCompletedProjects<T extends ProjectModel>(projects: T[]): T[] {
    return [...projects].sort((a, b) =>
        (b.actualEndDate ?? '').localeCompare(a.actualEndDate ?? '') || byTitle(a, b)
    );
}

/**
 * Completed projects of one year (by actual end date). `year` undefined keeps
 * every completed project ("Visa alla"). Projects without an actual end date
 * are kept, so a completed project never disappears from every year view.
 */
export function filterCompletedByYear<T extends ProjectModel>(projects: T[], year?: number): T[] {
    if (year === undefined) return projects;
    const prefix = String(year);
    return projects.filter(p => !p.actualEndDate || p.actualEndDate.startsWith(prefix));
}

/** One building's projects within a board column. */
export interface BuildingGroup<T> {
    buildingKey: string;
    projects: T[];
}

/** A building's group starts expanded when it holds fewer projects than this. */
export const GROUP_EXPANDED_BELOW = 5;

/**
 * Groups an already sorted column by building. Groups appear in the order of
 * their first project, so the column's sort (e.g. most urgent first) decides
 * which building leads; within a group the column order is kept.
 */
export function groupByBuilding<T extends { buildingKey: string }>(projects: T[]): BuildingGroup<T>[] {
    const groups = new Map<string, BuildingGroup<T>>();
    projects.forEach(project => {
        const group = groups.get(project.buildingKey);
        if (group) group.projects.push(project);
        else groups.set(project.buildingKey, { buildingKey: project.buildingKey, projects: [project] });
    });
    return Array.from(groups.values());
}
